import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@apps/clos-sessions/environments';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArchiveDTO, ArchiveSourceDTO, CreateArchiveDTO } from './types.dto';
import { ClosArchive, ClosArchivePriceDetails } from './types';

export interface CreateArchiveInput {
  name?: string;
  guid?: string;
}

export interface GetArchivesInput {
  take: number;
  after: number;
}

export interface GetArchivesOutput {
  hasNextChunk: boolean;
  list: ClosArchive[];
}

export interface SetRoomPriceInput {
  archive: { id: string };
  price: Partial<ClosArchivePriceDetails>;
}

@Injectable({
  providedIn: 'root',
})
export class ArchivesApi {
  private baseUrl = `${environment.api.baseUrl}/v1/room`;

  constructor(private http: HttpClient) {}

  createArchive({ name, guid }: CreateArchiveInput): Observable<ClosArchive> {
    return this.http
      .post<{ data: CreateArchiveDTO }>(`${this.baseUrl}`, {
        is_cloud_images: true,
        // techdebt https://github.com/CLOS-Inc/CLOS_Frontend/issues/569
        role: 'model',
        type: ArchiveSourceDTO.sharing,
        name,
      })
      .pipe(
        map(({ data }) => {
          return this.toClosArchive({
            ...data,
            model_ids: [],
            logo: '',
            logo_name: '',
            date: new Date().toISOString(),
            image_uploaded_count: 0,
            image_count: 0,
            retouch_image_count: 0,
            dropbox_image_count: 0,
            local_image_count: 0,
            inner_gallery_images_count: 0,
            video_count: 0,
            is_owner: true,
            archive_link: data.link || '',
            payment_link: '',
            is_prices_created: false,
            is_available_for_all: false,
            buttons: [],
            album_price: 0,
            photo_price: 0,
          });
        }),
        map((archive) => ({
          ...archive,
          guid,
        })),
      );
  }

  getArchiveById(archiveId: string): Observable<ClosArchive> {
    return this.http
      .get<{ data: ArchiveDTO }>(`${this.baseUrl}/${encodeURIComponent(archiveId)}/details`)
      .pipe(map(({ data }) => this.toClosArchive(data)));
  }

  getArchives(input: GetArchivesInput): Observable<GetArchivesOutput> {
    return this.http
      .get<{ data: ArchiveDTO[] }>(`${this.baseUrl}/my`, {
        params: {
          limit: input.take,
          skip: input.after,
        },
      })
      .pipe(
        switchMap(({ data }) => {
          const list = data.map((archiveDto) => this.toClosArchive(archiveDto));

          if (list.length < input.take) {
            return of({
              hasNextChunk: false,
              list,
            });
          }

          return this.getPaginationChunkState({ after: input.after + input.take }).pipe(
            map(({ hasNextChunk }) => ({
              hasNextChunk,
              list,
            })),
          );
        }),
      );
  }

  bulkRemoveById(archiveIds: string[]): Observable<unknown> {
    return this.http.delete(this.baseUrl, {
      body: {
        ids: archiveIds,
      },
    });
  }

  setRoomPrice(input: SetRoomPriceInput): Observable<unknown> {
    const { archive, price } = input;
    return this.http.post(`${this.baseUrl}/${encodeURIComponent(archive.id)}/prices`, {
      ...(price.collection ? { album_price: price.collection } : {}),
      ...(price.singlePhoto ? { photo_price: price.singlePhoto } : {}),
    });
  }

  protected getPaginationChunkState(
    input: Pick<GetArchivesInput, 'after'>,
  ): Observable<Pick<GetArchivesOutput, 'hasNextChunk'>> {
    return this.http
      .get<{ data: ArchiveDTO[] }>(`${this.baseUrl}/my`, {
        params: {
          limit: 1,
          skip: input.after,
        },
      })
      .pipe(
        map(({ data }) => ({
          hasNextChunk: data.length > 0,
        })),
      );
  }

  protected toClosArchive(input: ArchiveDTO): ClosArchive {
    return {
      id: input._id,
      name: input.name,
      type: input.type,
      createdAt: input.date,
      expiredAt: input.expire_at,
      owner: {
        id: input.owner_id,
        name: input.owner_name,
      },
      shareOptions: {
        paidLink: input.payment_link || '',
        publicLink: input.archive_link || '',
      },
      logo: {
        url: input.logo || '',
        name: input.logo_name || '',
      },
      public: input.is_available_for_all,
      stats: {
        dropboxImageCount: input.dropbox_image_count,
        imageUploadedCount: input.image_uploaded_count,
        innerGalleryImagesCount: input.inner_gallery_images_count,
        localImageCount: input.local_image_count,
        sharingImageCount: input.retouch_image_count,
        totalImageCount: input.image_count,
        videoCount: input.video_count,
      },
      userRelations: {
        models: (input.model_ids || []).map((id) => ({ id })),
        observers: (input.participants || []).map((id) => ({ id })),
        photographs: [{ id: input.photograph_id || '' }],
      },
      guid: '',
      commerceDetails: {
        isPriced: input.is_prices_created,
        price: {
          collection: input.album_price || 0,
          singlePhoto: input.photo_price || 0,
        },
      },
    };
  }
}
